import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { prefectures } from '../../const';
import { formSessionStorageKey } from './const';
import { RegisterInput } from './types';
import { birthDateValidator } from './validators/birthDateValidator';
import { confirmPasswordValidator } from './validators/confirmPasswordValidator';
import { emailValidator } from './validators/emailValidator';
import { maxLengthValidator } from './validators/maxLengthValidator';
import { passwordValidator } from './validators/passwordValidator';
import { requiredTextValidator } from './validators/requiredTextValidator';
import { termsValidator } from './validators/termsValidator';
import { zipcodeValidator } from './validators/zipcodeValidator';

@Component({
  templateUrl: './form.component.html',
  styleUrls: ['../../../style/global.css', './form.component.css'],
  selector: 'app-register-form',
})
export class FormComponent implements OnInit {
  @Input() years: {
    label: string;
    value?: unknown;
  }[] = [];
  @Input() months: {
    label: string;
    value?: unknown;
  }[] = [];
  @Input() dates: {
    label: string;
    value?: unknown;
  }[] = [];
  @Input() termsUrl = '';

  @Input() errorsFromResponse: Record<string, string> = {};

  genders = [
    {
      label: '男性',
      value: 'male',
    },
    {
      label: '女性',
      value: 'female',
    },
  ];

  prefectures = [{ label: '都道府県を選択', value: 'undefined' }].concat(
    prefectures.map((prefecture) => ({
      label: prefecture,
      value: prefecture,
    }))
  );

  passwordToggle = {
    password: false,
    confirm: false,
  };

  registerForm = this.fb.group({
    familyName: [
      '',
      [
        requiredTextValidator('姓を入力してください。'),
        maxLengthValidator({ fieldName: '姓', maxLength: 20 }),
      ],
    ],
    givenName: [
      '',
      [
        requiredTextValidator('名を入力してください。'),
        maxLengthValidator({ fieldName: '名', maxLength: 20 }),
      ],
    ],
    email: [
      '',
      [
        requiredTextValidator('メールアドレスを入力してください。'),
        emailValidator,
      ],
    ],
    password: this.fb.group(
      {
        rawPassword: [
          '',
          [
            requiredTextValidator('パスワードを入力してください。'),
            passwordValidator,
          ],
        ],
        confirmPassword: [''],
      },
      { validators: [confirmPasswordValidator] }
    ),
    birthDate: this.fb.group(
      {
        year: ['', requiredTextValidator('誕生年を入力してください。')],
        month: ['', requiredTextValidator('誕生月を入力してください。')],
        date: ['', requiredTextValidator('誕生日を入力してください。')],
      },
      { validators: [birthDateValidator] }
    ),
    gender: ['', requiredTextValidator('性別を正しく選択してください。')],
    zipcode: [
      '',
      [
        requiredTextValidator('郵便番号を入力してください。'),
        zipcodeValidator(() => this.setAddressUsingZipcode()),
      ],
    ],
    address: this.fb.group({
      prefecture: [''],
      city: [''],
      town: [''],
      building: [''],
    }),
    acceptTerm: [false, termsValidator],
  });
  @Output() register = new EventEmitter<RegisterInput>();

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    const content = window.sessionStorage.getItem(formSessionStorageKey);
    if (!content) {
      return;
    }
    try {
      const parsed = JSON.parse(content);
      if (parsed && typeof parsed === 'object') {
        const { zipcode, ...fields } = parsed;
        if (zipcode) {
          // WORKAROUND: バリデーションを無効にしないと郵便番号設定時に住所補完を実行してしまう。
          // 郵便番号に対する住所は一意に決まるわけではないので、意図しない入力を防ぐためにバリデーションを一時無効化する
          const field = this.registerForm.get('zipcode');
          const validator = field?.validator;
          field?.clearValidators();
          field?.setValue(zipcode);
          field?.markAsPristine();
          field?.markAsUntouched();
          field?.setValidators(validator ?? []);
        }
        this.registerForm.patchValue(fields);
      }
    } catch (error) {
      return;
    }
  }

  saveOnChange(): void {
    const serialized = JSON.stringify(this.registerForm.value);
    window.sessionStorage.setItem(formSessionStorageKey, serialized);
  }

  updateBirthYear(year: string) {
    this.registerForm.patchValue({
      birthDate: {
        year,
      },
    });
  }
  updateBirthMonth(month: string) {
    this.registerForm.patchValue({
      birthDate: {
        month,
      },
    });
  }
  updateBirthDate(date: string) {
    this.registerForm.patchValue({
      birthDate: {
        date,
      },
    });
  }

  updateGender(gender: string) {
    this.registerForm.patchValue({
      gender,
    });
  }

  updatePrefecture(prefecture: string) {
    this.registerForm.patchValue({
      address: {
        prefecture: prefecture === 'undefined' ? '' : prefecture,
      },
    });
  }

  async setAddressUsingZipcode() {
    // 郵便番号から住所情報を取得し設定する
    const control = this.registerForm.get('zipcode');
    if (!control) {
      return;
    }
    const zipcode = control.value;
    const endpoint = `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${zipcode}`;
    const resp = await fetch(endpoint).catch(() => null);
    if (!resp) {
      alert('住所情報の取得に失敗しました。');
      return;
    }
    switch (resp.status) {
      case 200: {
        const data = (await resp.json()) as {
          results:
            | {
                address1: string;
                address2: string;
                address3: string;
                kana1: string;
                kana2: string;
                kana3: string;
                prefcode: string;
                zipcode: string;
              }[]
            | null;
        };

        if (!data.results) {
          alert('郵便番号が正しくありません。');
          return;
        }
        const address = data.results[0];
        this.registerForm.patchValue({
          address: {
            prefecture: address.address1,
            city: address.address2,
            town: address.address3,
          },
        });

        return;
      }
      case 400: {
        alert('郵便番号が正しくありません。');
        return;
      }
      default: {
        alert('現在住所情報の取得はできません。');
        return;
      }
    }
  }

  shouldShowError(control: AbstractControl | null): boolean {
    if (!control?.errors) {
      return false;
    }
    return control.touched || control.dirty;
  }

  getErrorMessage(path: string, defaultMessage: string = ''): string {
    const control = this.registerForm.get(path);

    if (control?.invalid && !(control.touched || control.dirty)) {
      return defaultMessage;
    }

    const errors = control?.errors ?? {};
    for (const error of Object.values(errors)) {
      return String(error);
    }

    return '';
  }

  getAllErrors(control: FormGroup): { path: string; message: string }[] {
    if (control.valid) {
      return [];
    }
    const errors: { path: string; message: string }[] = [];
    for (const [path, subControl] of Object.entries(control.controls)) {
      if (subControl instanceof FormGroup) {
        errors.push(
          ...this.getAllErrors(subControl).map((v) => ({
            path: `${path}.${v.path}`,
            message: v.message,
          }))
        );
      }
      errors.push(
        ...Object.values(subControl.errors ?? {}).map((v) => ({
          path,
          message: String(v),
        }))
      );
    }
    return errors;
  }

  validateManually(path: string) {
    const control = this.registerForm.get(path);
    if (!control) {
      return;
    }
    control.updateValueAndValidity();
    control.markAsTouched();
  }

  listErrorsFromResponse(): string[] {
    return Object.values(this.errorsFromResponse).filter((x) => !!x);
  }

  onSubmit() {
    // 送信前にSessionStorageの内容を削除する
    // ブラウザを終了すれば消える内容だが、不要な情報は明示的に抱えないようにする
    window.sessionStorage.removeItem(formSessionStorageKey);

    this.register.emit(this.registerForm.value);
  }
}
